import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { ConfigurationByNameDocument } from '~/client/graph/operations/generated/Configurations.generated'
import { SaveProcoreDataForProjectDocument } from '~/client/graph/operations/generated/Procore.generated'
import {
  IDailyLogFieldMappingInput,
  IIntegrationWorkflowTypeConfigInput,
  IPermitFieldSourceInput,
  IProcoreIntegrationConfigurationInput,
  SitePermitSpecialFieldType,
} from '~/client/graph/types.generated'

import { IntegrationAppName } from '../../../enums/IntegrationAppName'
import Localization from '../../../localization/LocalizationManager'
import ProcoreService, {
  IProcoreCompany,
  IProcoreProject,
  IProcoreUserBasic,
} from '../../../services/ProcoreService'
import { toast } from '../../../utils/toast'
import { isEqualDeep } from '../../../utils/util'
import EventsStore from '../../EventStore/Events.store'
import GraphExecutorStore from '../GraphExecutor.store'

interface IRawFieldSource {
  PermitFieldId?: string
  permitFieldId?: string
  PermitSpecialField?: SitePermitSpecialFieldType
  permitSpecialField?: SitePermitSpecialFieldType
}

interface IRawFieldMapping {
  DailyLogFieldName?: string
  dailyLogFieldName?: string
  FieldSources?: IRawFieldSource[]
  fieldSources?: IRawFieldSource[]
}

interface IRawWorkflowType {
  TypeId?: string
  typeId?: string
  InitialType?: string
  initialType?: string
  FieldMappings?: IRawFieldMapping[]
  fieldMappings?: IRawFieldMapping[]
}

interface IProcoreProjectConfig {
  projectId: string
  companyId: string
}

interface IProcoreIntegrationConfig {
  projectId: string
  procoreProjects: IProcoreProjectConfig[]
  isEnabled: boolean
  workflowTypes?: IIntegrationWorkflowTypeConfigInput[]
}

const defaultProcoreConfiguration: IProcoreIntegrationConfig = {
  projectId: '',
  isEnabled: false,
  procoreProjects: [],
}

export default class ProcoreIntegrationStore {
  @observable public procoreConfiguration: IProcoreIntegrationConfig =
    defaultProcoreConfiguration
  @observable public isInitializing: boolean = false
  @observable public isSaving: boolean = false
  @observable public procoreUser: IProcoreUserBasic = null
  @observable public procoreCompanies: IProcoreCompany[] = []
  @observable public isLoadingData: boolean = false
  @observable public selectedCompanyId: string = null

  private procoreService: ProcoreService
  // True when the most recent loadProcoreData() threw (e.g. invalid/expired
  // token, network failure) as opposed to succeeding with zero companies. Lets
  // loginToProcore tell "could not connect" apart from "no company access".
  private lastDataLoadFailed = false

  public constructor(
    private readonly eventsStore: EventsStore,
    private readonly graphExecutorStore: GraphExecutorStore,
  ) {
    makeObservable(this)
    this.procoreService = new ProcoreService(this.eventsStore.appState)
  }

  public init() {
    if (!this.isIntegrationEnabled) {
      return
    }
    this.loadProcoreConfig().catch(error => {
      console.error('Failed to initialize Procore integration:', error)
    })
  }

  public get isIntegrationEnabled(): boolean {
    return this.eventsStore.appState.isIntegrationEnabled(
      IntegrationAppName.PROCORE,
    )
  }

  public get isIntegrationActive(): boolean {
    return this.procoreConfiguration?.isEnabled
  }

  public get isUserLoggedIn(): boolean {
    return !!this.procoreUser
  }

  public get availableCompanies(): { id: string; name: string }[] {
    return this.procoreCompanies
      .filter(c => c.isActive)
      .map(({ id, name }) => ({
        id,
        name,
      }))
  }

  public async loadProcoreData() {
    if (this.isLoadingData) {
      return
    }
    runInAction(() => {
      this.isLoadingData = true
    })

    this.lastDataLoadFailed = false
    try {
      const procoreCompanies =
        await this.procoreService.getPermittedCompaniesWithProjects()

      const companyId =
        this.procoreConfiguration.procoreProjects?.[0]?.companyId ||
        procoreCompanies?.[0]?.id

      const procoreUser = companyId
        ? await this.procoreService.getProcoreUserMe(companyId)
        : null

      runInAction(() => {
        this.procoreCompanies = procoreCompanies
        this.procoreUser = procoreUser
      })
      this.setDefaultCompanyAndProject()
    } catch (error) {
      console.error('Failed to load Procore data:', error)
      this.lastDataLoadFailed = true
      await this.logoutFromProcore()
    } finally {
      runInAction(() => {
        this.isLoadingData = false
      })
    }
  }

  public async reloadProcoreData(): Promise<void> {
    if (!this.isIntegrationEnabled || !this.isUserLoggedIn) {
      return
    }

    await this.loadProcoreData()
  }

  @action.bound
  private setDefaultCompanyAndProject() {
    if (this.procoreConfiguration.procoreProjects?.length > 0) {
      this.selectedCompanyId =
        this.procoreConfiguration.procoreProjects[0].companyId
      return
    }

    if (!this.procoreCompanies.length) {
      this.selectedCompanyId = null
      return
    }

    if (this.selectedCompany) {
      this.saveConfiguration(this.selectedCompany.id, '')
      return
    }

    const companyWithProjects = this.procoreCompanies.find(
      company => company?.projects.length > 0,
    )

    if (companyWithProjects) {
      this.saveConfiguration(
        companyWithProjects.id,
        companyWithProjects.projects[0].id,
      )
    } else {
      this.saveConfiguration(this.procoreCompanies[0].id, '')
    }
  }

  public async loadProcoreConfig(shouldResetToDefaultConfig: boolean = true) {
    try {
      const { data } = await this.graphExecutorStore.query(
        ConfigurationByNameDocument,
        { name: `project.${this.projectId}.integrations.procore` },
      )
      const configValue = data?.configuration?.value
      if (!configValue && shouldResetToDefaultConfig) {
        this.resetProcoreData()
        return
      }

      if (configValue) {
        const config = this.transformIntegrationConfigData(configValue)
        // Skipping the data load when the config is unchanged is only safe once
        // a session exists. Right after login the token is new and the user has
        // never been loaded, so an unchanged config must still trigger a load.
        if (
          isEqualDeep(this.procoreConfiguration, config) &&
          this.isUserLoggedIn
        ) {
          return
        }

        runInAction(() => {
          this.procoreConfiguration = config
          this.selectedCompanyId =
            config.procoreProjects?.[0]?.companyId || null
        })
      }

      await this.loadProcoreData()
    } catch (error) {
      console.error('Failed to retrieve Procore Integration data:', error)
      // The config fetch failed (loadProcoreData swallows its own errors), so
      // let loginToProcore report a connection failure rather than "no access".
      this.lastDataLoadFailed = true
    }
  }

  @action.bound
  public resetProcoreData() {
    this.procoreConfiguration = {
      ...defaultProcoreConfiguration,
      isEnabled: this.procoreConfiguration.isEnabled,
    }
    this.procoreUser = null
    this.procoreCompanies = []
    this.isLoadingData = false
    this.selectedCompanyId = null
  }

  public async saveConfiguration(
    companyId?: string,
    projectId?: string,
    workflowTypes?: IIntegrationWorkflowTypeConfigInput[],
  ) {
    runInAction(() => {
      this.isSaving = true
    })

    try {
      runInAction(() => {
        if (companyId) {
          this.selectedCompanyId = companyId
        }

        const targetCompanyId = companyId || this.selectedCompanyId
        if (projectId !== undefined && targetCompanyId) {
          this.procoreConfiguration.procoreProjects = [
            { projectId: projectId || '', companyId: targetCompanyId },
          ]
        }

        if (workflowTypes !== undefined) {
          this.procoreConfiguration.workflowTypes = workflowTypes
        }
      })

      const procoreProjects =
        this.procoreConfiguration.procoreProjects?.length > 0
          ? this.procoreConfiguration.procoreProjects
          : this.selectedCompanyId
            ? [{ projectId: '', companyId: this.selectedCompanyId }]
            : []

      const configData: IProcoreIntegrationConfigurationInput = {
        projectId: this.projectId,
        isEnabled: this.procoreConfiguration.isEnabled,
        procoreProjects,
        workflowTypes: this.procoreConfiguration.workflowTypes,
      }

      const { error } = await this.graphExecutorStore.mutate(
        SaveProcoreDataForProjectDocument,
        {
          procoreIntegration: configData,
        },
      )

      if (error) {
        console.error('Procore configuration save failed:', error)
        throw error
      }
    } catch (error) {
      console.error('Procore configuration save error:', error)
    } finally {
      runInAction(() => {
        this.isSaving = false
      })
    }
  }

  public async loginToProcore() {
    const previousState = {
      procoreUser: this.procoreUser,
      procoreCompanies: [...this.procoreCompanies],
    }

    runInAction(() => {
      this.isInitializing = true
    })

    try {
      if (this.isUserLoggedIn) {
        return
      }
      this.lastDataLoadFailed = false
      await this.procoreService.getProcoreToken()
      await this.loadProcoreConfig(false)

      // loadProcoreConfig/loadProcoreData swallow their own errors, so reaching
      // this line does not mean login succeeded. Distinguish the outcomes:
      //  - logged in           → success
      //  - data load threw     → could not connect / token invalid (retry)
      //  - loaded, no companies → authenticated but no company access
      if (this.isUserLoggedIn) {
        toast.success(Localization.translator.procoreLoginSuccess)
        return
      }

      toast.error(
        this.lastDataLoadFailed
          ? Localization.translator.procoreConnectionFailed
          : Localization.translator.procoreNoCompaniesAccess,
      )
    } catch (error) {
      console.error('Procore login failed:', error)
      runInAction(() => {
        this.procoreUser = previousState.procoreUser
        this.procoreCompanies = previousState.procoreCompanies
      })
      // getProcoreToken rejects when the popup is blocked or closed by the user;
      // surface it instead of failing silently.
      toast.error(
        (error instanceof Error && error.message) ||
          Localization.translator.procoreConnectionFailed,
      )
    } finally {
      runInAction(() => {
        this.isInitializing = false
      })
    }
  }

  public async logoutFromProcore() {
    await this.procoreService.logoutFromProcore()
    this.resetProcoreData()
  }

  public async toggleIntegration(isEnabled: boolean) {
    const previousState = this.procoreConfiguration.isEnabled

    runInAction(() => {
      this.procoreConfiguration.isEnabled = isEnabled
    })

    try {
      await this.saveConfiguration()

      if (isEnabled) {
        toast.success(Localization.translator.procoreIntegrationEnabled)
      }
    } catch (error) {
      runInAction(() => {
        this.procoreConfiguration.isEnabled = previousState
      })
      console.error('Failed to toggle integration:', error)
    }
  }

  public get selectedCompany(): IProcoreCompany {
    return this.selectedCompanyId
      ? this.procoreCompanies.find(c => c.id === this.selectedCompanyId)
      : null
  }

  public async setSelectedCompany(companyId: string) {
    const previousCompanyId = this.selectedCompanyId
    const previousProjects = [...this.procoreConfiguration.procoreProjects]

    runInAction(() => {
      this.selectedCompanyId = companyId
    })

    const filteredProjects = (
      this.procoreConfiguration.procoreProjects || []
    ).filter(project => project.companyId === companyId)

    runInAction(() => {
      this.procoreConfiguration.procoreProjects = !filteredProjects.length
        ? [{ projectId: '', companyId: companyId }]
        : filteredProjects
    })

    try {
      await this.saveConfiguration()
    } catch (error) {
      runInAction(() => {
        this.selectedCompanyId = previousCompanyId
        this.procoreConfiguration.procoreProjects = previousProjects
      })
      console.error('Failed to set selected company:', error)
    }
  }

  @computed
  public get selectedProjects(): IProcoreProject[] {
    if (
      !this.procoreConfiguration.procoreProjects?.length ||
      !this.selectedCompany
    ) {
      return []
    }

    return this.procoreConfiguration.procoreProjects.reduce(
      (acc: IProcoreProject[], p) => {
        if (p.companyId === this.selectedCompany.id) {
          const project = this.selectedCompany.projects.find(
            project => project.id === p.projectId,
          )
          if (project) acc.push(project)
        }
        return acc
      },
      [],
    )
  }

  @computed
  public get selectedProjectsMap(): Map<string, boolean> {
    return this.selectedProjects.reduce((map, project) => {
      map.set(project.id, true)
      return map
    }, new Map<string, boolean>())
  }

  public isProjectSelected(projectId: string): boolean {
    return this.selectedProjectsMap.has(projectId)
  }

  public async setSelectedProjects(projectIds: string[], companyId: string) {
    const previousProjects = [...this.procoreConfiguration.procoreProjects]

    runInAction(() => {
      this.procoreConfiguration.procoreProjects = !projectIds.length
        ? [{ projectId: '', companyId: companyId }]
        : projectIds.map(projectId => ({
            projectId,
            companyId,
          }))
    })
    try {
      await this.saveConfiguration()
    } catch (error) {
      runInAction(() => {
        this.procoreConfiguration.procoreProjects = previousProjects
      })
      console.error('Failed to set selected projects:', error)
    }
  }

  public async removeProcoreProject(projectId: string) {
    const previousProjects = [
      ...(this.procoreConfiguration.procoreProjects || []),
    ]
    runInAction(() => {
      this.procoreConfiguration.procoreProjects =
        !this.procoreConfiguration.procoreProjects.length &&
        this.selectedCompanyId
          ? [{ projectId: '', companyId: this.selectedCompanyId }]
          : this.procoreConfiguration.procoreProjects.filter(
              project => project.projectId !== projectId,
            )
    })

    try {
      await this.saveConfiguration()
    } catch (error) {
      runInAction(() => {
        this.procoreConfiguration.procoreProjects = previousProjects
      })
      console.error('Failed to remove project:', error)
    }
  }

  private get projectId(): string {
    return this.eventsStore.appState.activeProject.id
  }

  private transformIntegrationConfigData(
    rawConfig: string,
  ): IProcoreIntegrationConfig {
    const config = JSON.parse(rawConfig)
    return {
      projectId: this.projectId,
      isEnabled: config.IsEnabled ?? defaultProcoreConfiguration.isEnabled,
      procoreProjects: (
        config.ProcoreProjects ?? defaultProcoreConfiguration.procoreProjects
      ).map(
        (project: {
          ProjectId?: string
          CompanyId?: string
          projectId?: string
          companyId?: string
        }) => ({
          projectId: project.ProjectId ?? project.projectId,
          companyId: project.CompanyId ?? project.companyId,
        }),
      ),
      workflowTypes: this.transformWorkflowTypes(config.WorkflowTypes),
    }
  }

  private transformWorkflowTypes(
    workflowTypes: IRawWorkflowType[],
  ): IIntegrationWorkflowTypeConfigInput[] | undefined {
    if (!workflowTypes?.length) return undefined

    return workflowTypes.map(wt => ({
      typeId: wt.TypeId ?? wt.typeId,
      initialType: wt.InitialType ?? wt.initialType,
      fieldMappings: this.transformFieldMappings(
        wt.FieldMappings ?? wt.fieldMappings,
      ),
    }))
  }

  private transformFieldMappings(
    fieldMappings: IRawFieldMapping[] | null | undefined,
  ): IDailyLogFieldMappingInput[] | undefined {
    if (!fieldMappings?.length) return undefined

    return fieldMappings.map(fm => ({
      dailyLogFieldName: fm.DailyLogFieldName ?? fm.dailyLogFieldName,
      fieldSources: this.transformFieldSources(
        fm.FieldSources ?? fm.fieldSources,
      ),
    }))
  }

  private transformFieldSources(
    fieldSources: IRawFieldSource[] | null | undefined,
  ): IPermitFieldSourceInput[] | undefined {
    if (!fieldSources?.length) return undefined

    return fieldSources.map(fs => ({
      permitFieldId: fs.PermitFieldId ?? fs.permitFieldId,
      permitSpecialField: fs.PermitSpecialField ?? fs.permitSpecialField,
    }))
  }
}
